import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Github, Linkedin, Mail, Download, Code, Briefcase, User, Star,
  Database, Brain, BarChart, MessageCircle, Eye, Cloud, LineChart
} from 'lucide-react';

// Main App Component
function App() {
  const [activeSection, setActiveSection] = useState('home');

  // Define variants for section entrance animations
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  // Define variants for individual element animations
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  // Sample data for the portfolio
  const portfolioData = {
    name: "Emmanuel Boakye",
    title: "Data Scientist & Machine Learning Engineer",
    intro: "Passionate about leveraging AI and data to solve complex problems and build intelligent systems. Specializing in machine learning, statistical modeling, and data visualization.",
    about: "With a strong foundation in data and statistical analysis, and machine learning algorithms, I transform raw data into actionable insights and robust predictive models. My expertise spans across various domains, including natural language processing, recommender systems, and time series analysis. I am proficient in Python, R, and SQL, and experienced with frameworks like TensorFlow, PyTorch, and Scikit-learn. I thrive in dynamic environments and in teams where innovation and AI data-driven decision-making are valued.",
    skills: [
      { name: "Python", icon: <Code size={50} className="text-blue-600" /> },
      { name: "R", icon: <LineChart size={50} className="text-blue-600" /> },
      { name: "SQL", icon: <Database size={50} className="text-blue-600" /> },
      { name: "Machine Learning", icon: <Brain size={50} className="text-blue-600" /> },
      { name: "Deep Learning", icon: <Brain size={50} className="text-blue-600" /> },
      { name: "Data Visualization", icon: <BarChart size={50} className="text-blue-600" /> },
      { name: "NLP", icon: <MessageCircle size={50} className="text-blue-600" /> },
      { name: "Cloud Platforms (AWS)", icon: <Cloud size={50} className="text-blue-600" /> },
    ],
    experience: [
      {
        title: "Postgraduate Research Assitant",
        company: "Loughborough University",
        duration: "Nov 2024 - Mar 2025",
        description: "Engineered Python data pipelines for UK physical inactivity and mental health survey data, reducing processing time by 2 days while maintaining GDPR compliance. Conducted EDA using pandas, matplotlib, and plotly to identify 3 underactive regions that shaped policy recommendations. Developed machine learning models with scikit-learn to predict key risk factors (age, income, education) for 7,500 participants and implemented RPA automation that cut manual data ingestion by 50%. Contributed to peer-reviewed manuscript submission.",
      },
      {
        title: "Data Quality Manager",
        company: "St. Louis College of Education",
        duration: "Oct 2023 - Sep 2024",
        description: "Project-managed implementation of scalable SQL-driven data quality standards across 3 departments, improving accuracy in 18,000+ student records. Created real-time dashboards that informed 5 quarterly strategic reviews and secured senior leadership buy-in for new governance measures. Led stakeholder workshops training faculty and staff on data management practices, achieving 100% compliance with national data protection regulations. Designed automated validation checks and Power BI reports that reduced reporting time by 40%.",
      },
      {
        title: "Financial Analyst Intern",
        company: "Cal Bank PLC",
        duration: "Jul 2023 - Oct 2023",
        description: "Supported Data and Finance teams in analyzing financial statements, market trends, and risk factors, contributing to £200k cost-savings recommendations. Parsed and validated financial data from annual reports, integrating revenue data into compliance checks and forecasting models under Bank of Ghana regulations. Conducted loan portfolio compliance checks that flagged 50 high-risk accounts and reduced non-performing loans by 12%. Co-designed Excel automation templates for internal financial reports and executive summaries, cutting report preparation time by 30%.",
      },
      {
        title: "Data Analyst Intern",
        company: "Edify Technologies",
        duration: "Jun 2022 - Nov 2022",
        description: "Conducted exploratory data analysis using Python (pandas, matplotlib, seaborn) to uncover customer behavior patterns that informed new product launch strategy. Collaborated with senior analysts to automate monthly reporting tasks, reducing manual generation time by 40% and enabling deeper analytical focus. Built interactive Power BI dashboards with the team to track client project metrics and KPIs, identifying two under-resourced projects and preventing $45k in potential cost overruns.",
      },
    ],
    projects: [
      {
        title: "Spotify Song Dataset Analyser",
        description: "Designed a full-stack data analysis pipeline using SQLite and Python to clean, store, and analyze 50,000+ Spotify tracks, uncovering trends in genre popularity, artist rankings, and audio features. Developed insight-driven visualizations with Matplotlib to compare artist popularity across genres and created an interactive frontend enabling users to explore top artists and most-played songs by custom timeframes through dynamic charts and visualisations.",
        link: "https://github.com/ChurchillonData/Spotify_clone_with_full-stack_functionality",
        technologies: ["Python", "NLP", "SQLite", "Pandas"],
        image: "https://images.unsplash.com/photo-1624814448280-0f2d7d399b07?q=80&w=2021&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
      },
      {
        title: "Income and Demographics Analysis Using US Census Data",
        description: "This project analyzes US Census data to explore income disparities, demographic trends, and political correlations. It includes data preprocessing, fairness evaluations, income prediction using machine learning (with Gradient Boosting achieving 78% accuracy), and an independent hypothesis on occupation and work hours. Key insights reveal significant income inequality influenced by sex, race, education, and birthplace, with strong links between socio-economic status and 2020 election outcomes.",
        link: "https://github.com/ChurchillonData/Data_Mining",
        technologies: ["Python", "Tableau", "PowerBI"],
        image: "https://images.unsplash.com/photo-1604496464355-a5032e3b47e0?q=80&w=1925&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
      },
      {
        title: "Breast Cancer Classification using Machine Learning",
        description: "Implemented logistic regression on the Wisconsin Breast Cancer dataset using scikit-learn with preprocessing and a 70/30 split plus 5-fold cross-validation, achieving 94% accuracy; conducted feature selection and ROC analysis.",
        link: "https://github.com/ChurchillonData/Breast_Cancer_Diagnosis",
        technologies: ["Python", "PyTorch", "Scikit-learn"],
        image: "https://plus.unsplash.com/premium_photo-1722945689852-0bcf3669b894?q=80&w=2026&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
      },
    ],
    resumeLink: "https://drive.google.com/file/d/1llVjOfXDJWqYRzWQhIRVbhGcJpxc4aWW/view?usp=drivesdk",
    linkedinLink: "http://linkedin.com/in/emmanuel-boakye-gh",
    githubLink: "https://github.com/ChurchillonData",
    email: "boakyemmanuel1@outlook.com",
    profilePicture: "https://i.imgur.com/QahWb9K.jpeg"
  };

  return (
    <div className="min-h-screen text-gray-900 font-inter antialiased bg-gradient-to-br from-blue-200 via-purple-200 to-pink-200">
      {/* Navigation Bar */}
      <nav className="fixed w-full z-20 bg-white/90 shadow-lg px-6 py-4 backdrop-blur-md">
        <div className="container mx-auto flex justify-between items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="text-2xl font-bold text-blue-600"
          >
            {portfolioData.name.split(' ')[0]}'s Portfolio
          </motion.div>
          <ul className="flex space-x-6">
            {['home', 'about', 'skills', 'experience', 'projects', 'contact'].map((section) => (
              <motion.li
                key={section}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 * (['home', 'about', 'skills', 'experience', 'projects', 'contact'].indexOf(section)) }}
              >
                <a
                  href={`#${section}`}
                  onClick={() => setActiveSection(section)}
                  className={`text-lg font-medium hover:text-blue-600 transition-colors duration-300 ${activeSection === section ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-700'}`}
                >
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </a>
              </motion.li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center p-8 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-5" style={{ backgroundImage: 'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=")' }}></div>

        <motion.div
          className="relative z-10 text-center bg-white/70 backdrop-blur-sm p-8 rounded-xl shadow-2xl max-w-3xl border border-blue-200"
          initial="hidden"
          animate="visible"
          variants={sectionVariants}
        >
          <motion.h1 className="text-5xl font-extrabold text-blue-800 mb-4" variants={itemVariants}>
            {portfolioData.name}
          </motion.h1>
          <motion.p className="text-2xl text-gray-700 mb-6" variants={itemVariants}>
            {portfolioData.title}
          </motion.p>
          <motion.p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto" variants={itemVariants}>
            {portfolioData.intro}
          </motion.p>
          <motion.div className="flex justify-center space-x-6" variants={itemVariants}>
            <motion.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              whileTap={{ scale: 0.9 }}
            >
              <a
                href={portfolioData.linkedinLink}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-gray-100 hover:bg-emerald-600 rounded-full transition-colors duration-300 shadow-lg backdrop-blur-sm inline-block"
              >
                <Linkedin size={30} className="text-blue-700 hover:text-white" />
              </a>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.1, rotate: -5 }}
              whileTap={{ scale: 0.9 }}
            >
              <a
                href={portfolioData.githubLink}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-gray-100 hover:bg-emerald-600 rounded-full transition-colors duration-300 shadow-lg backdrop-blur-sm inline-block"
              >
                <Github size={30} className="text-gray-800 hover:text-white" />
              </a>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <a
                href={`mailto:${portfolioData.email}`}
                className="p-3 bg-gray-100 hover:bg-emerald-600 rounded-full transition-colors duration-300 shadow-lg backdrop-blur-sm inline-block"
              >
                <Mail size={30} className="text-rose-600 hover:text-white" />
              </a>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a
                href={portfolioData.resumeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-rose-600 hover:bg-rose-700 text-white rounded-full transition-colors duration-300 flex items-center space-x-2 px-6 shadow-lg inline-flex"
              >
                <Download size={20} />
                <span>Resume</span>
              </a>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="container mx-auto py-20 px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={sectionVariants}
          className="bg-white p-8 rounded-xl shadow-lg border border-gray-200"
        >
          <h2 className="text-4xl font-bold text-blue-700 mb-6 text-center">
            <User size={36} className="inline-block mr-2 text-blue-600" />
            About Me
          </h2>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={{
              visible: { transition: { staggerChildren: 0.2 } }
            }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={itemVariants}>
              <img
                src={portfolioData.profilePicture}
                alt="Profile Picture"
                className="rounded-full shadow-lg mx-auto w-96 h-96 object-contain border-4 border-blue-500"
              />
            </motion.div>
            <motion.div variants={itemVariants} className="text-lg text-gray-700 leading-relaxed text-justify">
              <p className="mb-4">{portfolioData.about}</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="container mx-auto py-20 px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={sectionVariants}
          className="bg-white p-8 rounded-xl shadow-lg border border-gray-200"
        >
          <h2 className="text-4xl font-bold text-blue-700 mb-10 text-center">
            <Star size={36} className="inline-block mr-2 text-blue-600" />
            Skills
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {portfolioData.skills.map((skill, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-6 bg-blue-50 rounded-lg shadow-md border border-blue-100 transform hover:scale-105 transition-transform duration-300"
                variants={itemVariants}
              >
                {skill.icon}
                <p className="mt-4 text-xl font-semibold text-gray-800 text-center">{skill.name}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="container mx-auto py-20 px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={sectionVariants}
          className="bg-white p-8 rounded-xl shadow-lg border border-gray-200"
        >
          <h2 className="text-4xl font-bold text-blue-700 mb-10 text-center">
            <Briefcase size={36} className="inline-block mr-2 text-blue-600" />
            Experience
          </h2>
          <div className="space-y-12">
            {portfolioData.experience.map((job, index) => (
              <motion.div
                key={index}
                className="flex flex-col md:flex-row bg-blue-50 p-8 rounded-lg shadow-md border border-blue-100 items-start md:items-center transform hover:scale-105 transition-transform duration-300"
                variants={itemVariants}
              >
                <div className="md:w-1/4 mb-4 md:mb-0 md:pr-8 text-center md:text-left">
                  <h3 className="text-xl font-bold text-blue-600">{job.company}</h3>
                  <p className="text-md text-gray-600">{job.duration}</p>
                </div>
                <div className="md:w-3/4">
                  <h4 className="text-2xl font-semibold text-gray-800 mb-2">{job.title}</h4>
                  <p className="text-md text-gray-700 leading-relaxed">{job.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="container mx-auto py-20 px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={sectionVariants}
          className="bg-white p-8 rounded-xl shadow-lg border border-gray-200"
        >
          <h2 className="text-4xl font-bold text-blue-700 mb-10 text-center">
            <Code size={36} className="inline-block mr-2 text-blue-600" />
            Projects
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {portfolioData.projects.map((project, index) => (
              <motion.div
                key={index}
                className="bg-blue-50 p-6 rounded-lg shadow-md border border-blue-100 flex flex-col transform hover:scale-105 transition-transform duration-300"
                variants={itemVariants}
              >
                <img
                  src={project.image}
                  alt={`${project.title} Preview`}
                  className="w-full h-40 object-cover rounded-md mb-4"
                />
                <h3 className="text-2xl font-semibold text-gray-800 mb-3">{project.title}</h3>
                <p className="text-md text-gray-700 mb-4 flex-grow">{project.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="text-lg font-bold text-gray-800">Technologies: </span>
                  {project.technologies.map((tech, techIndex) => (
                    <span key={techIndex} className="bg-blue-200 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors duration-300 self-start"
                >
                  View Project
                </a>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="container mx-auto py-20 px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={sectionVariants}
          className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 text-center max-w-2xl mx-auto"
        >
          <h2 className="text-4xl font-bold text-blue-700 mb-6">
            <Mail size={36} className="inline-block mr-2 text-blue-600" />
            Contact
          </h2>
          <motion.p className="text-lg text-gray-700 mb-8" variants={itemVariants}>
            Feel free to reach out if you have any questions, opportunities, or just want to connect!
          </motion.p>
          <motion.div className="flex flex-col space-y-4" variants={itemVariants}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a
                href={`mailto:${portfolioData.email}`}
                className="flex items-center justify-center space-x-3 px-8 py-4 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-all duration-300 transform inline-flex text-xl font-medium"
              >
                <Mail size={24} />
                <span>{portfolioData.email}</span>
              </a>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a
                href={portfolioData.linkedinLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-3 px-8 py-4 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 transform inline-flex text-xl font-medium"
              >
                <Linkedin size={24} />
                <span>Connect on LinkedIn</span>
              </a>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a
                href={portfolioData.githubLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-3 px-8 py-4 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-900 transition-all duration-300 transform inline-flex text-xl font-medium"
              >
                <Github size={24} />
                <span>View my GitHub</span>
              </a>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-8 mt-16 rounded-t-xl shadow-inner">
        <div className="container mx-auto text-center text-gray-400 text-sm">
          <p>&copy; {new Date().getFullYear()} {portfolioData.name}. All rights reserved.</p>
          <p className="mt-2">Designed with ❤️ HTML, CSS, React and Tailwind.</p>
        </div>
      </footer>
    </div>
  );
}

export default App; 